import React from 'react';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { useNotifications } from '@/hooks/useNotifications';

const NotificationsDropdown: React.FC = () => {
  const {
    notifications,
    loading,
    error,
    unreadCount,
    markAsRead,
    markAllAsRead,
    formatRelativeTime
  } = useNotifications({
    autoRefresh: true,
    refreshInterval: 30000, // 30 seconds
    initialPagination: { page: 1, limit: 20, sortBy: 'timestamp', sortOrder: 'desc' }
  });


  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'success':
        return <div className="notification-type-indicator notification-type-success w-1.5 h-1.5" />;
      case 'warning':
        return <div className="notification-type-indicator notification-type-warning w-1.5 h-1.5" />;
      case 'error':
        return <div className="notification-type-indicator notification-type-error w-1.5 h-1.5" />;
      default:
        return <div className="notification-type-indicator notification-type-info w-1.5 h-1.5" />;
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <div className="notifications-dropdown group relative cursor-pointer mt-[3px]">
          <div className="relative">
            <div className="w-[2.4rem] h-[2.4rem] text-black dark:text-white group-hover:text-gray-300 transition-colors duration-200 flex items-center justify-center">
              <svg className="w-[2rem] h-[2rem]" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clipPath="url(#clip0_5636_5713)">
                  <path d="M8.10976 6.37434C8.08058 6.33919 8.05193 6.30403 8.0238 6.2701C7.63709 5.80235 7.40312 5.52005 7.40312 4.19589C7.40312 3.51034 7.23912 2.94784 6.91586 2.52597C6.6775 2.21431 6.35529 1.97788 5.9306 1.80315C5.92514 1.80011 5.92026 1.79613 5.91619 1.79138C5.76343 1.27985 5.34543 0.937256 4.87398 0.937256C4.40254 0.937256 3.98471 1.27985 3.83195 1.79085C3.82787 1.79542 3.82306 1.79929 3.81771 1.80227C2.82666 2.21026 2.34502 2.99302 2.34502 4.19536C2.34502 5.52005 2.11141 5.80235 1.72434 6.26958C1.69621 6.3035 1.66756 6.33795 1.63838 6.37381C1.563 6.46471 1.51525 6.57531 1.50076 6.6925C1.48628 6.80969 1.50567 6.92859 1.55664 7.0351C1.6651 7.26362 1.89625 7.40547 2.1601 7.40547H7.5898C7.85242 7.40547 8.08199 7.26379 8.1908 7.03633C8.24199 6.92979 8.26156 6.8108 8.2472 6.69347C8.23284 6.57614 8.18514 6.46539 8.10976 6.37434V6.37434Z" fill="currentColor" />
                  <path d="M4.87509 8.8125C5.1291 8.81229 5.37832 8.74334 5.59631 8.61296C5.81431 8.48258 5.99295 8.29563 6.11329 8.07193C6.11896 8.06121 6.12176 8.04921 6.12142 8.03709C6.12108 8.02496 6.11761 8.01314 6.11135 8.00275C6.10508 7.99237 6.09624 7.98378 6.08568 7.97782C6.07512 7.97186 6.06319 7.96874 6.05107 7.96875H3.69947C3.68732 7.96871 3.67538 7.97181 3.66479 7.97776C3.6542 7.9837 3.64534 7.99229 3.63905 8.00268C3.63277 8.01307 3.62928 8.02491 3.62894 8.03704C3.62859 8.04918 3.63139 8.0612 3.63706 8.07193C3.75739 8.2956 3.93601 8.48254 4.15397 8.61292C4.37193 8.7433 4.62111 8.81226 4.87509 8.8125Z" fill="currentColor" />
                </g>
                <defs>
                  <clipPath id="clip0_5636_5713">
                    <rect width="9" height="9" fill="white" transform="translate(0.375 0.375)" />
                  </clipPath>
                </defs>
              </svg>
            </div>
            {unreadCount > 0 && (
              <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium">
                {unreadCount}
              </div>
            )}
          </div>
        </div>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent 
        className="w-[30rem] bg-white dark:bg-[#272727] border-none rounded-lg shadow-lg notifications-dropdown p-0"
        align="end"
        sideOffset={8}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-600">
          <h3 className="text-[1.6rem] font-semibold text-gray-800 dark:text-white">
            Notifications
          </h3>
          {unreadCount > 0 && (
            <Button 
              variant="ghost"
              size="sm"
              onClick={() => markAllAsRead()}
              className="text-[#57E8BA] text-[1.2rem] hover:text-[#57E8BA]/90 hover:bg-[transparet] markRead h-auto p-1"
            >
              Mark all as read
            </Button>
          )}
        </div>

        {/* Notifications List */}
        <div className="max-h-96 overflow-y-auto notiWrap notifications-scrollbar">
          {loading ? (
            <div className="p-4 text-center text-gray-500 dark:text-gray-400">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#57E8BA] mx-auto"></div>
              <span className="mt-2 block">Loading notifications...</span>
            </div>
          ) : error ? (
            <div className="p-4 text-center text-red-500 dark:text-red-400">
              <span className="block">Error loading notifications</span>
              <span className="text-sm mt-1 block">{error}</span>
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-4 text-center text-gray-500 dark:text-gray-400">
              No notifications
            </div>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-gray-600">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`notification-item px-4 py-3 hover:bg-gray-200 dark:hover:bg-[#2a3d38] cursor-pointer ${
                    !notification.isRead 
                      ? 'notification-unread bg-blue-50 dark:bg-[#233631]' 
                      : ''
                  }`}
                  style={{
                    borderLeft: `3px solid ${!notification.isRead ? '#57E8BA' : 'transparent'}`
                  }}
                  onClick={() => markAsRead(notification.id)}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex items-center justify-center w-6 h-6 mt-1">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className={`text-[1.3rem] font-medium truncate ${
                          !notification.isRead 
                            ? 'text-gray-900 dark:text-white' 
                            : 'text-gray-600 dark:text-gray-300'
                        }`}>
                          {notification.title}
                        </span>
                      </div>
                      <p className="text-[1.1rem] text-gray-600 dark:text-gray-400 line-clamp-2 leading-relaxed mt-1">
                        {notification.message}
                      </p>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-[1rem] text-gray-400 dark:text-gray-500">
                          {formatRelativeTime(notification.timestamp)}
                        </span>
                        {notification.actionUrl && (
                          <span className="text-[0.9rem] text-[#57E8BA] hover:text-[#57E8BA]/80">
                            View →
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-600 flex justify-center">
          <Button 
            className="w-fit text-center text-[1.2rem] h-fit p-[1rem] px-[1.2rem] capitalize text-black bg-[#57E8BA] hover:bg-[#57E8BA]/90"
          >
            View all notifications
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationsDropdown;
